<script>
  import { getContext, onMount, tick } from "svelte";
  import { splitString } from "./anonymizationTextSplit.js";
  import { Loading } from "carbon-components-svelte";
  import { recordingStore } from "@stores/recordingStore.js";
  import Header from "lib/components/Header.svelte";
  import PercentageBar from "lib/components/PercentageBar.svelte";
  import Modal from "lib/components/Modal.svelte";
  import ButtonIconOnly from "lib/components/buttonIconOnly.svelte";
  import { SectionWrapper } from "paxray-components-svelte";
  import AnonElement from "../components/AnonElement.svelte";
  import PaxbotChat from "lib/components/paxbot/PaxbotChat.svelte";
  import { fetchWithSession } from "../../../../lib/util/fetchWithSession";
  import AnonymizationReviewFilters from "./AnonymizationReviewFilters.svelte";
  import AnonymizationReviewPagination from "./AnonymizationReviewPagination.svelte";
  import AnonymizationShortcutPanel from "./AnonymizationShortcutPanel.svelte";
  import AnonymizationRuleModal from "./AnonymizationRuleModal.svelte";
  import ProjectStepActions from "../components/ProjectStepActions.svelte";
  import { PROJECT_STAGES } from "@utils/stagesAndSteps.js";

  export let selectedReviewType = 1;

  const idproject = new URLSearchParams(window.location.search).get("idproject");
  const showOptions = [
    { value: "all", text: "All" },
    { value: "checked", text: "Checked" },
    { value: "unchecked", text: "Not Checked" },
  ];
  const sortOptions = [
    { value: "length", text: "Length" },
    { value: "name", text: "Name" },
    { value: "apps", text: "Apps" },
  ];
  const pageSizeOptions = ["5", "10", "15", "20", "25"].map((value) => ({
    text: value,
    value,
  }));
  const anonymizationOptions = {
    main: {
      title: "Main Anon",
      options: ["NUMBER", "NAME", "COMPANY", "ID"].map((value) => ({ value })),
    },
    time: {
      title: "Time",
      options: ["DATE", "YEAR", "MONTH", "WEEK", "DAY", "TIME"].map((value) => ({ value })),
    },
    context: {
      title: "Context",
      options: ["LOCATION", "URL", "PATH"].map((value) => ({ value })),
    },
    misc: {
      title: "Misc",
      options: [
        "TEXT", "PRODUCT", "AMOUNT", "EMAIL", "VERSION", "DOCUMENT", "CELL",
      ].map((value) => ({ value })),
    },
  };
  const anonymizationTypes = new Set(
    Object.values(anonymizationOptions).flatMap((group) =>
      group.options.map((option) => option.value),
    ),
  );

  let projectStageId;
  let safeProposalOpen = false;
  let safeProposalRow = null;
  let safeProposalBusy = false;
  let safeProposalError = "";
  let rows = [];
  let appMappings = new Map();
  let selectedScope = 0;
  let requestedScope = "";
  let selectedShow = "all";
  let selectedSort = "length";
  let selectedPageSize = "10";
  let selectedApplicationIds = [];
  let selectedProcess = "";
  let selectedSubprocess = "";
  let selectedTask = "";
  let selectedSubtask = "";
  let selectedTeam = "";
  let selectedAssignedOption = "";
  let searchQuery = "";
  let page = 0;
  let loading = true;
  let loadVersion = 0;
  let paxbotOpen = false;
  let keyboardRowId = "";
  let keyboardTokenIndex = 0;
  let keyboardNavigationActive = false;
  let keyboardSelectionAnchor = null;
  let selectedElements = [];
  let selectedElementKeys = [];
  let selectedElementValues = [];
  let shiftPressed = false;
  let elementRuleModalOpen = false;
  let elementRuleLoading = false;
  let elementRuleReview = null;
  let showElementType = true;
  let showApplicationName = true;
  let toastMsg;
  let saveSuccessful = false;
  const role = getContext("role");
  const completionStep = PROJECT_STAGES.TASK_REGISTRATION.steps.CHECK_ANON;
  const completionStage = PROJECT_STAGES.TASK_REGISTRATION.id;

  $: selectedElementKeys = selectedElements.map((selected) => selected.key);
  $: selectedElementValues = selectedElements.map((selected) => ({
    type: selected.token.type,
    removeAnonType: selected.token.value,
  }));

  let processes = [];
  let listOfSubprocesses = [];
  let listOfTasks = [];
  let listOfSubtasks = [];
  let availableSubtasks = [];
  $: ({ processes, listOfSubprocesses, listOfTasks, listOfSubtasks } = $recordingStore);
  $: availableSubtasks = listOfSubtasks
    .filter((subtask) => rows.some((row) => row.subtasks.some((item) => String(item.id) === String(subtask.id))))
    .sort((first, second) => first.name.localeCompare(second.name));

  $: scope = selectedScope === 0 ? "assigned" : selectedScope === 1 ? "global" : "unassigned";
  $: if (projectStageId && requestedScope !== scope) {
    requestedScope = scope;
    loadRows(scope);
  }

  $: applicationOptions = [...new Set(rows.flatMap((row) => row.displayApps))]
    .sort((a, b) => a.localeCompare(b))
    .map((app) => ({ id: app, text: app }));
  $: {
    const validApplicationIds = selectedApplicationIds.filter((id) =>
      applicationOptions.some((option) => option.id === id),
    );
    if (validApplicationIds.length !== selectedApplicationIds.length) {
      selectedApplicationIds = validApplicationIds;
    }
  }
  $: selectedApplicationLabel = selectedApplicationIds.length === 0
    ? "All"
    : selectedApplicationIds.length === 1
      ? selectedApplicationIds[0]
      : `${selectedApplicationIds.length} selected`;

  $: filteredRows = rows
    .filter((row) => selectedShow === "all" || row.checked === (selectedShow === "checked"))
    .filter((row) => !searchQuery || [row.original, row.anonymized, ...row.displayApps]
      .some((value) => value.toLowerCase().includes(searchQuery.toLowerCase())))
    .filter((row) => selectedApplicationIds.length === 0
      || row.displayApps.some((app) => selectedApplicationIds.includes(app)))
    .filter(matchesHierarchyFilters)
    .sort(compareRows);
  $: pageSize = Number(selectedPageSize);
  $: totalPages = Math.ceil(filteredRows.length / pageSize);
  $: if (page >= totalPages) page = Math.max(totalPages - 1, 0);
  $: visibleRows = filteredRows.slice(page * pageSize, (page + 1) * pageSize);
  $: checkedEntries = rows.filter((row) => row.checked).length;
  $: subtaskIds = [...new Set(rows.flatMap((row) => row.subtasks.map((subtask) => String(subtask.id))))];
  $: checkedSubtasks = subtaskIds.filter((subtaskId) => {
    const subtaskRows = rows.filter((row) =>
      row.subtasks.some((subtask) => String(subtask.id) === subtaskId),
    );
    return subtaskRows.length > 0 && subtaskRows.every((row) => row.checked);
  }).length;

  onMount(async () => {
    const [progressResponse, mappingResponse] = await Promise.all([
      fetchWithSession(`/api/damprojects/DAMProject/${idproject}/progress`),
      fetchWithSession("/api/damprojects/getAppnameMapping"),
    ]);
    const progress = await progressResponse.json();
    const mappings = await mappingResponse.json();
    appMappings = new Map(
      mappings.map((mapping) => [String(mapping.applicationname), mapping.clearname]),
    );
    projectStageId = progress.find((stage) => stage.stage === "TASK_REGISTRATION")?.id;
  });

  async function loadRows(nextScope) {
    loading = true;
    const version = ++loadVersion;
    try {
      const response = await fetchWithSession(
        `/api/damprojects/ui-element-anonymization-preview-scoped?project_stage_id=${projectStageId}&scope=${nextScope}`,
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Unable to load UI elements");
      if (version !== loadVersion) return;
      rows = result.rows.map(toDisplayRow);
      page = 0;
    } catch (error) {
      console.error("Unable to load UI-element anonymization", error);
      rows = [];
    } finally {
      if (version === loadVersion) loading = false;
    }
  }

  function toDisplayRow(row) {
    const displayApps = row.apps.map((app) => appMappings.get(String(app)) || app);
    return {
      ...row,
      displayApps,
      tokens: buildTokens(row.original, row.anonymized),
    };
  }

  function placeholderType(value) {
    const match = String(value || "").trim().match(/^<<<([^>]+)>>>$/);
    if (match) return match[1];
    const plainValue = String(value || "").trim();
    return anonymizationTypes.has(plainValue) ? plainValue : "";
  }

  function createToken(value, type, systemPlaceholder = false) {
    return { value, type, systemPlaceholder };
  }

  function isSeparator(value) {
    return /^[_(),.:\-]+$/.test(String(value || "").trim());
  }

  function alignAnonymizedTokens(originalTokens, anonymizedTokens) {
    const memo = new Map();
    function align(originalIndex, anonymizedIndex) {
      const key = `${originalIndex}:${anonymizedIndex}`;
      if (memo.has(key)) return memo.get(key);
      if (anonymizedIndex === anonymizedTokens.length) {
        return originalIndex === originalTokens.length ? [] : null;
      }

      const placeholder = placeholderType(anonymizedTokens[anonymizedIndex]);
      if (placeholder) {
        const systemPlaceholder = /^<<<[^>]+>>>$/.test(anonymizedTokens[anonymizedIndex])
          && !anonymizationTypes.has(placeholder);
        const remainingTokens = anonymizedTokens.length - anonymizedIndex - 1;
        for (
          let end = originalIndex + 1;
          end <= originalTokens.length - remainingTokens;
          end += 1
        ) {
          const tail = align(end, anonymizedIndex + 1);
          if (tail) {
            const result = [
              ...Array.from(
                { length: end - originalIndex },
                () => ({ type: placeholder, systemPlaceholder }),
              ),
              ...tail,
            ];
            memo.set(key, result);
            return result;
          }
        }
      } else if (
        originalIndex < originalTokens.length
        && originalTokens[originalIndex].trim() === anonymizedTokens[anonymizedIndex].trim()
      ) {
        const tail = align(originalIndex + 1, anonymizedIndex + 1);
        if (tail) {
          const result = [
            { type: originalTokens[originalIndex], systemPlaceholder: false },
            ...tail,
          ];
          memo.set(key, result);
          return result;
        }
      }

      memo.set(key, null);
      return null;
    }
    return align(0, 0);
  }

  function buildTokens(original, anonymized) {
    const originalTokens = splitString(original);
    const anonymizedTokens = splitString(anonymized);
    if (anonymizedTokens.length === 0) {
      return originalTokens.map((value) => createToken(value, value));
    }
    const alignedTypes = alignAnonymizedTokens(originalTokens, anonymizedTokens);
    if (alignedTypes) {
      return originalTokens.map((value, index) => ({
        ...createToken(
          value,
          isSeparator(value) ? "" : alignedTypes[index].type,
          !isSeparator(value) && alignedTypes[index].systemPlaceholder,
        ),
      }));
    }
    return originalTokens.map((value, index) => {
      const maskedValue = anonymizedTokens[index] || value;
      const placeholder = placeholderType(maskedValue);
      return createToken(
        value,
        isSeparator(value) ? "" : placeholder || value,
        /^<<<[^>]+>>>$/.test(maskedValue) && !anonymizationTypes.has(placeholder),
      );
    });
  }

  function joinTokens(tokens) {
    return tokens.map((token) => {
      if (isSeparator(token.value)) return token.value;
      const trailingSeparator = token.value.match(/[_\s-]+$/)?.[0] || "";
      if (token.systemPlaceholder) {
        return `<<<${token.type}>>>${trailingSeparator}`;
      }
      if (!anonymizationTypes.has(token.type)) return token.type;
      return `<<<${token.type}>>>${trailingSeparator}`;
    }).join("");
  }

  function compareRows(first, second) {
    if (selectedSort === "name") return first.original.localeCompare(second.original);
    if (selectedSort === "apps") return second.app_count - first.app_count;
    return second.original.length - first.original.length;
  }

  function matchesHierarchyFilters(row) {
    const hierarchyFilterActive = selectedProcess || selectedSubprocess || selectedTask || selectedSubtask;
    if (!hierarchyFilterActive) return true;
    return row.subtasks.some((subtask) => {
      const subtaskData = listOfSubtasks.find((item) => String(item.id) === String(subtask.id));
      const task = listOfTasks.find((item) => String(item.id) === String(subtaskData?.task_id));
      const subprocess = listOfSubprocesses.find((item) => String(item.id) === String(task?.subprocess_id));
      const process = processes.find((item) => String(item.id) === String(subprocess?.process_id));
      return (!selectedProcess || String(process?.id) === String(selectedProcess))
        && (!selectedSubprocess || String(subprocess?.id) === String(selectedSubprocess))
        && (!selectedTask || String(task?.id) === String(selectedTask))
        && (!selectedSubtask || String(subtaskData?.id) === String(selectedSubtask));
    });
  }

  async function saveRow(row) {
    const response = await fetchWithSession(
      "/api/damprojects/ui-element-anonymization-preview/group",
      {
        method: "PATCH",
        body: JSON.stringify({
          member_ids: row.member_ids,
          anonymized: row.anonymized,
          checked: row.checked,
        }),
      },
    );
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || "Unable to save UI element");
  }

  async function changeToken(row, token) {
    token.systemPlaceholder = false;
    row.anonymized = joinTokens(row.tokens);
    row.checked = false;
    rows = [...rows];
    try {
      await saveRow(row);
    } catch (error) {
      console.error("Unable to save UI-element change", error);
    }
  }

  function getSelectedElementKey(row, tokenIndex) {
    return `${row.id}:${tokenIndex}`;
  }

  function handleTokenSelection(row, token, tokenIndex, event) {
    const key = getSelectedElementKey(row, tokenIndex);
    const selectedElement = { key, row, token };
    const selectedIndex = selectedElements.findIndex((selected) => selected.key === key);
    if (event?.shiftKey || shiftPressed) {
      selectedElements = selectedIndex >= 0
        ? selectedElements.filter((_, index) => index !== selectedIndex)
        : [...selectedElements, selectedElement];
      return;
    }
    selectedElements = [selectedElement];
  }

  function clearSelectionOutsideElements(event) {
    if (!event.target?.closest(".anon-element")) selectedElements = [];
  }

  async function applyTypeToSelectedTokens(type) {
    const changedRows = new Map();
    for (const { row, token } of selectedElements) {
      token.type = type;
      token.systemPlaceholder = false;
      row.anonymized = joinTokens(row.tokens);
      row.checked = false;
      changedRows.set(row.id, row);
    }
    rows = [...rows];
    selectedElements = [];
    await Promise.all([...changedRows.values()].map(saveRow));
  }

  function handleTokenTypeChanged(row, token) {
    if (selectedElements.length > 1) {
      applyTypeToSelectedTokens(token.type);
      return;
    }
    changeToken(row, token);
  }

  async function removeSelectedAnonymizations() {
    const changedRows = new Map();
    for (const { row, token } of selectedElements) {
      token.type = token.value;
      token.systemPlaceholder = false;
      row.anonymized = joinTokens(row.tokens);
      row.checked = false;
      changedRows.set(row.id, row);
    }
    rows = [...rows];
    selectedElements = [];
    await Promise.all([...changedRows.values()].map(saveRow));
  }

  // Same flow as the standard titles in the window title check.
  function safeTextTooltip(row) {
    if (row.safe_status === "approved") return "Approved as standard text";
    if (row.safe_status === "pending") return "Remove standard text proposal";
    if (row.original !== row.anonymized) return "Only unchanged texts can be standard texts";
    return "Standard text of the program?";
  }

  // Proposing a text asks for confirmation first, withdrawing does not.
  function requestToggleSafeText(row) {
    if (row.safe_status || row.original !== row.anonymized) {
      toggleSafeText(row).catch((error) => console.error("Unable to update UI allowlist proposal", error));
      return;
    }
    safeProposalRow = row;
    safeProposalError = "";
    safeProposalOpen = true;
  }

  async function confirmSafeText() {
    if (!safeProposalRow || safeProposalBusy) return;
    safeProposalBusy = true;
    try {
      await toggleSafeText(safeProposalRow);
      safeProposalOpen = false;
      safeProposalRow = null;
    } catch (error) {
      safeProposalError = error.message;
    } finally {
      safeProposalBusy = false;
    }
  }

  function cancelSafeText() {
    safeProposalOpen = false;
    safeProposalRow = null;
  }

  // Proposes a generic, unchanged UI text as safe for all projects. An admin
  // approves it in the admin settings, approved texts are pre-checked in new
  // projects. Approved texts can only be removed in the admin settings.
  async function toggleSafeText(row) {
    if (row.safe_status === "approved") return;
    const withdraw = row.safe_status === "pending";
    if (!withdraw && row.original !== row.anonymized) return;
    if (!withdraw && !row.checked) toggleChecked(row);
    const response = await fetchWithSession("/api/damprojects/safe-ui-elements/propose", {
      method: withdraw ? "DELETE" : "POST",
      // fetchWrapper sets the JSON header only for POST and PATCH, DELETE needs it too.
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ member_ids: row.member_ids }),
      suppressErrorToast: !withdraw,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || "Unable to update UI allowlist proposal");
    // Only this row changes, reloading all rows took a minute and reset the page.
    row.safe_status = withdraw ? null : "pending";
    rows = [...rows];
  }

  async function toggleChecked(row) {
    row.checked = !row.checked;
    rows = [...rows];
    try {
      await saveRow(row);
      if (row.checked && /<<<[A-Z][A-Z0-9_/-]*>>>/.test(row.anonymized || "")) {
        await createElementRule(row, true);
      }
    } catch (error) {
      row.checked = !row.checked;
      rows = [...rows];
      console.error("Unable to update UI-element check", error);
    }
  }

  async function markVisibleAsChecked() {
    const updates = visibleRows.filter((row) => !row.checked);
    updates.forEach((row) => (row.checked = true));
    rows = [...rows];
    await Promise.all(updates.map(saveRow));
    for (const row of updates) {
      if (/<<<[A-Z][A-Z0-9_/-]*>>>/.test(row.anonymized || "")) {
        await createElementRule(row, false);
      }
    }
    if (page + 1 < totalPages) page += 1;
  }

  async function createElementRule(row, showModal) {
    elementRuleLoading = showModal;
    elementRuleModalOpen = showModal;
    elementRuleReview = { source: row, preview: null };
    try {
      const response = await fetchWithSession("/api/damprojects/ui-element-anonymization-rule/preview", {
        method: "POST",
        body: JSON.stringify({
          source_id: row.id,
          member_ids: row.member_ids,
          sections: row.tokens.map((token) => ({
            value: token.value,
            type: token.type,
            is_anonymized: token.systemPlaceholder || anonymizationTypes.has(token.type),
          })),
        }),
      });
      const preview = await response.json();
      if (!response.ok) throw new Error(preview.error || "Unable to generate UI-element rule");
      elementRuleReview = { source: row, preview };
      elementRuleLoading = false;
      if (!showModal) await applyElementRule();
    } catch (error) {
      elementRuleLoading = false;
      elementRuleModalOpen = false;
      console.error("Unable to generate UI-element rule", error);
    }
  }

  async function applyElementRule() {
    const { source, preview } = elementRuleReview || {};
    if (!preview) return;
    elementRuleLoading = true;
    try {
      const response = await fetchWithSession("/api/damprojects/ui-element-anonymization-rule/apply", {
        method: "POST",
        body: JSON.stringify({
          source_id: preview.source_id,
          search: preview.search,
          member_ids: source?.member_ids || [preview.source_id],
          selected_ids: preview.candidates.filter((candidate) => candidate.selected).map((candidate) => candidate.id),
          rejected_ids: preview.candidates.filter((candidate) => !candidate.selected).map((candidate) => candidate.id),
        }),
      });
      if (!response.ok) throw new Error("Unable to apply UI-element rule");
      elementRuleModalOpen = false;
      elementRuleReview = null;
      await loadRows(scope);
    } catch (error) {
      console.error("Unable to apply UI-element rule", error);
    } finally {
      elementRuleLoading = false;
    }
  }

  function selectableTokenIndexes(row) {
    return row.tokens.map((token, index) => isSeparator(token.value) ? -1 : index).filter((index) => index >= 0);
  }

  async function revealKeyboardTarget() {
    await tick();
    document.querySelector(
      `[data-keyboard-target="${keyboardRowId}-${keyboardTokenIndex}"]`,
    )?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }

  function setKeyboardCursor(row, tokenIndex) {
    keyboardNavigationActive = true;
    keyboardRowId = row.id;
    keyboardTokenIndex = tokenIndex;
    keyboardSelectionAnchor = null;
  }

  function selectKeyboardRange(row, startIndex, endIndex) {
    const firstIndex = Math.min(startIndex, endIndex);
    const lastIndex = Math.max(startIndex, endIndex);
    selectedElements = row.tokens
      .map((token, tokenIndex) => ({ token, tokenIndex }))
      .filter(({ token, tokenIndex }) => !isSeparator(token.value) && tokenIndex >= firstIndex && tokenIndex <= lastIndex)
      .map(({ token, tokenIndex }) => ({
        key: getSelectedElementKey(row, tokenIndex),
        row,
        token,
      }));
  }

  async function handleKeyboardNavigation(event) {
    if (event.key === "Escape") {
      window.dispatchEvent(new Event("close-anonymization-menu"));
      return;
    }
    if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Enter", " "].includes(event.key)) return;
    const target = event.target;
    if (
      target?.matches?.("input, textarea, select, [contenteditable='true']")
      || target?.closest?.(".options-list, .paxbot_chat_window")
    ) return;
    if ((event.key === "Enter" || event.key === " ") && target?.closest?.("button")) return;

    const availableRows = visibleRows.filter((row) => selectableTokenIndexes(row).length);
    if (!availableRows.length) return;
    let rowIndex = availableRows.findIndex((row) => row.id === keyboardRowId);
    if (!keyboardNavigationActive || rowIndex < 0) {
      const firstRow = availableRows[0];
      setKeyboardCursor(firstRow, selectableTokenIndexes(firstRow)[0]);
      event.preventDefault();
      await revealKeyboardTarget();
      if (event.key === "Enter") {
        document.querySelector(`[data-keyboard-target="${keyboardRowId}-${keyboardTokenIndex}"] .custom-select`)?.click();
      }
      return;
    }

    const row = availableRows[rowIndex];
    const indexes = selectableTokenIndexes(row);
    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      rowIndex = Math.max(0, Math.min(
        availableRows.length - 1,
        rowIndex + (event.key === "ArrowDown" ? 1 : -1),
      ));
      const nextRow = availableRows[rowIndex];
      setKeyboardCursor(nextRow, selectableTokenIndexes(nextRow)[0]);
    } else if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      const currentPosition = Math.max(0, indexes.indexOf(keyboardTokenIndex));
      const nextPosition = Math.max(0, Math.min(
        indexes.length - 1,
        currentPosition + (event.key === "ArrowRight" ? 1 : -1),
      ));
      const nextTokenIndex = indexes[nextPosition];
      if (event.shiftKey || shiftPressed) {
        if (keyboardSelectionAnchor?.rowId !== row.id) {
          keyboardSelectionAnchor = { rowId: row.id, tokenIndex: keyboardTokenIndex };
        }
        selectKeyboardRange(row, keyboardSelectionAnchor.tokenIndex, nextTokenIndex);
      } else {
        keyboardSelectionAnchor = null;
      }
      keyboardTokenIndex = nextTokenIndex;
    } else if (event.key === " " && event.shiftKey) {
      requestToggleSafeText(row);
    } else if (event.key === " ") {
      const nextRowId = availableRows[rowIndex + 1]?.id;
      toggleChecked(row);
      await tick();
      const updatedRows = visibleRows.filter(
        (candidate) => selectableTokenIndexes(candidate).length,
      );
      const nextRow = updatedRows.find((candidate) => candidate.id === nextRowId)
        || updatedRows[Math.min(rowIndex, updatedRows.length - 1)];
      if (nextRow) setKeyboardCursor(nextRow, selectableTokenIndexes(nextRow)[0]);
    } else {
      document.querySelector(`[data-keyboard-target="${row.id}-${keyboardTokenIndex}"] .custom-select`)?.click();
    }
    event.preventDefault();
    revealKeyboardTarget();
  }

  function navigateSelectedSubtask(direction) {
    if (availableSubtasks.length < 2) return;
    const currentIndex = availableSubtasks.findIndex((item) => String(item.id) === String(selectedSubtask));
    const nextIndex = (currentIndex + direction + availableSubtasks.length) % availableSubtasks.length;
    selectedSubtask = availableSubtasks[nextIndex].id;
  }

  async function requestPaxbotAnswer(question, history, responseLanguage) {
    const response = await fetchWithSession("/api/damprojects/paxbot/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        question,
        history: history.slice(-2),
        context: {
          page: "UI-element anonymization review",
          projectId: idproject,
          progress: { checkedEntries, totalEntries: rows.length, checkedSubtasks, totalSubtasks: subtaskIds.length },
          visibleEntries: visibleRows.slice(0, 25).map((row) => ({ application: row.displayApps.join(", "), original: row.original, anonymized: row.anonymized, checked: row.checked })),
          responseLanguage,
        },
      }),
    });
    const result = await response.json();
    if (!response.ok || result.status === "error") throw new Error(result.message || "Anonymization assistant request failed");
    return result.answer;
  }
</script>

<svelte:window
  on:keydown={(event) => {
    if (event.key === "Shift") shiftPressed = true;
    handleKeyboardNavigation(event);
  }}
  on:keyup={(event) => {
    if (event.key === "Shift") {
      shiftPressed = false;
      keyboardSelectionAnchor = null;
    }
  }}
  on:blur={() => {
    shiftPressed = false;
    keyboardSelectionAnchor = null;
  }}
  on:mousedown={clearSelectionOutsideElements}
/>

<Header
  section="Anonymization Check"
  area="Discovery"
  breadcrumb="Anonymization Check"
  tooltipText="Review captured UI elements and confirm that required anonymization checks are complete."
>
  <div class="header-status">
    <PercentageBar total={subtaskIds.length} totalChecked={checkedSubtasks} text="Subtasks checked" />
    <PercentageBar total={rows.length} totalChecked={checkedEntries} text="Entries checked" />
    <button type="button" class="header-assistant-button" class:active={paxbotOpen} title="Anonymization assistant" aria-label="Open anonymization assistant" on:click={() => (paxbotOpen = !paxbotOpen)}>
      <img src="/icons/bot.svg" alt="" />
    </button>
    {#if $role?.role === "admin"}
      <ProjectStepActions
        {idproject}
        step={completionStep}
        stage={completionStage}
        showGoBackButton={false}
        bind:toastMsg
        bind:saveSuccessful
        text="Complete Anonymization Check"
        size="sm"
        completionWarning="This marks the anonymization check phase as complete. This action cannot be undone."
      />
    {/if}
  </div>
</Header>

<SectionWrapper fullscreen seamless fitScreen>
  <div slot="main" class="anonymization-section">
    <div class="content-wrapper">
    <AnonymizationReviewFilters
      {idproject}
      bind:selectedProcess
      bind:selectedSubprocess
      bind:selectedTask
      bind:selectedSubtask
      bind:selectedTeam
      bind:selectedAssignedOption
      bind:selectedLevel={selectedReviewType}
      bind:selectedShow
      bind:selectedApplications={selectedApplicationIds}
      bind:selectedSort
      bind:selectedPageSize
      {showOptions}
      {applicationOptions}
      applicationLabel={selectedApplicationLabel}
      {sortOptions}
      {pageSizeOptions}
      subtaskNavigationEnabled={availableSubtasks.length > 1}
      onNavigateSubtask={navigateSelectedSubtask}
      onSearch={(value) => (searchQuery = value)}
    />

    {#if loading}
      <div class="state"><Loading small withOverlay={false} description="Loading UI elements" /><span>Loading UI elements...</span></div>
    {:else if visibleRows.length === 0}
      <div class="state">No UI elements match the selected filters.</div>
    {:else}
      <section class="rows">
        {#each visibleRows as row}
          <div class="row" class:checked={row.checked}>
            {#if showApplicationName}
              <AnonElement
                readElement="Application"
                anontype={row.app_count === 1 ? row.displayApps[0] : `${row.app_count} Apps`}
                appTitle={row.displayApps.join(", ")}
                firstElement={true}
              />
            {/if}
            {#each row.tokens as token, tokenIndex}
              <div
                class="token-cell"
                class:separator-cell={isSeparator(token.value)}
                class:keyboard-target-active={keyboardNavigationActive && keyboardRowId === row.id && keyboardTokenIndex === tokenIndex}
                data-keyboard-target={`${row.id}-${tokenIndex}`}
                on:mousedown={() => {
                  if (!isSeparator(token.value)) setKeyboardCursor(row, tokenIndex);
                }}
              >
              {#if isSeparator(token.value)}
                <span class="separator-placeholder-line" aria-hidden="true"></span>
                <span class="token-separator">{token.value}</span>
              {:else}
                <AnonElement
                  readElement={token.value}
                  bind:anontype={token.type}
                  anontypeOptionsMap={anonymizationOptions}
                  removeAnonType={token.value}
                  selected={selectedElementKeys.includes(getSelectedElementKey(row, tokenIndex))}
                  multiSelected={selectedElements.length > 1}
                  {selectedElementValues}
                  onSelection={(event) => handleTokenSelection(row, token, tokenIndex, event)}
                  on:typeChanged={() => handleTokenTypeChanged(row, token)}
                  on:removeSelected={removeSelectedAnonymizations}
                  hideLastChar={/[\s_]$/.test(token.value)}
                />
              {/if}
              </div>
            {/each}
            {#if showElementType}
              <span class="element-type-label" title={`UI element type: ${row.type || "unknown"}`}>
                Type: {row.type || "unknown"}
              </span>
            {/if}
            <span class="row-end" class:checked={row.checked}></span>
            <ButtonIconOnly
              long
              icon={row.checked ? "close" : "check"}
              title={row.checked ? "Mark as not checked" : "Mark as checked"}
              ariaLabel={row.checked ? "Mark as not checked" : "Mark as checked"}
              instantTooltip
              tooltipEmphasis="not"
              on:click={() => toggleChecked(row)}
            />
            <span
              class="safe-text-toggle"
              class:pending={row.safe_status === "pending"}
              class:approved={row.safe_status === "approved"}
            >
              <ButtonIconOnly
                long
                icon={row.safe_status ? "done_all_dark" : "done_all"}
                disable={row.safe_status === "approved" ||
                  (!row.safe_status && row.original !== row.anonymized)}
                title={safeTextTooltip(row)}
                ariaLabel={safeTextTooltip(row)}
                on:click={() => requestToggleSafeText(row)}
              />
            </span>
          </div>
        {/each}
        <AnonymizationReviewPagination
          previousText={page > 0 ? `Previous ${selectedPageSize}` : "Previous task"}
          nextText={`Next ${selectedPageSize}`}
          previousDisabled={page === 0}
          nextDisabled={page + 1 >= totalPages}
          onMarkShown={markVisibleAsChecked}
          onPrevious={() => (page -= 1)}
          onNext={() => (page += 1)}
        />
      </section>
    {/if}
    </div>
  <AnonymizationShortcutPanel
    showTypeSetting
    showApplicationSetting
    bind:showApplicationName
    bind:showElementType
  />
</div>
</SectionWrapper>

<PaxbotChat
  bind:open={paxbotOpen}
  heading="Anonymization Assistant"
  subheading="Masks and elements review"
  starterQuestions={["What should I review first?", "Explain this proposed masking", "Which entries still need attention?"]}
  requestAnswer={requestPaxbotAnswer}
/>

<AnonymizationRuleModal
  bind:open={elementRuleModalOpen}
  loading={elementRuleLoading}
  review={elementRuleReview}
  itemLabel="UI element"
  on:close={() => {
    elementRuleModalOpen = false;
    elementRuleLoading = false;
    elementRuleReview = null;
  }}
  on:apply={applyElementRule}
/>

<Modal
  bind:open={safeProposalOpen}
  actions
  autowidth
  preventCloseOnClickOutside
  modalHeading="Does this text only show a standard label of the program?"
  primaryButtonText="Yes, standard text"
  primaryButtonValidations={safeProposalBusy}
  primaryButtonAction={confirmSafeText}
  secondaryButtonAction={cancelSafeText}
  on:close={cancelSafeText}
>
  <div class="standard-text-guide">
    <p>“{safeProposalRow?.original || ""}”</p>
    <p><strong>Yes</strong>, for example Save, Open, Settings or a help menu entry.</p>
    <p><strong>No</strong>, if the text contains</p>
    <ul>
      <li>names of people, customers or colleagues</li>
      <li>file, folder or project names</li>
      <li>customer or case numbers, dates or search terms</li>
      <li>internal names of your company, for example team or channel names</li>
    </ul>
    <p>After a review, this text is checked automatically in future projects.</p>
    {#if safeProposalError}<p role="alert">{safeProposalError}</p>{/if}
  </div>
</Modal>

<style>
  .header-status, .row, .state { display: flex; align-items: center; }
  .header-status { gap: 0.5rem; }
  .anonymization-section { position: relative; display: flex; width: 100%; height: 100%; min-width: 0; min-height: 0; background: var(--pax-white); }
  .content-wrapper { display: flex; min-width: 0; min-height: 0; flex: 1; flex-direction: column; overflow: hidden; padding: 0.81rem 1.19rem 0.81rem 0.88rem; }
  .rows { display: flex; min-height: 0; flex: 1; flex-direction: column; align-items: flex-start; gap: 0.38rem; padding-top: 0.75rem; overflow: auto; }
  .row { width: fit-content; min-width: 0; min-height: 4rem; padding-left: 0.625rem; justify-content: flex-start; gap: 0.75rem; border-left: 0.625rem solid #ef8484; border-radius: 0.3125rem; background: var(--pax-smoke); align-self: flex-start; }
  .row.checked { border-left-color: #8ef095; }
  .row-end { align-self: stretch; width: 0.5625rem; min-width: 0.5625rem; margin-left: -0.2rem; margin-right: -0.45rem; border-radius: 0 3px 3px 0; background: #ef8484; }
  .row-end.checked { background: #8ef095; }
  .safe-text-toggle { display: flex; }
  .safe-text-toggle.pending :global(.button_wrapper),
  .safe-text-toggle.pending :global(.button_wrapper:hover) { background-color: var(--pax-blue-new); }
  .safe-text-toggle.approved :global(.button_wrapper),
  .safe-text-toggle.approved :global(.button_wrapper:hover) { background-color: var(--pax-green-new); opacity: 1; }
  .standard-text-guide { display: flex; flex-direction: column; gap: 0.5rem; font-size: 0.875rem; line-height: 1.5; }
  .standard-text-guide p { margin: 0; }
  .standard-text-guide ul { margin: 0; padding-left: 1.25rem; list-style: disc; }
  .standard-text-guide li { display: list-item; }
  .token-cell { position: relative; align-self: stretch; display: flex; align-items: center; margin: 0; border-radius: 0.35rem; }
  .separator-cell { width: 0.6rem; height: 4rem; flex: 0 0 0.6rem; margin: 0; flex-direction: column; justify-content: space-evenly; align-items: center; }
  .separator-cell + .token-cell { margin-left: 0; }
  .separator-placeholder-line { display: block; height: 1rem; flex: 0 0 1rem; }
  .token-separator { display: flex; width: 100%; height: 1.75rem; flex: 0 0 1.75rem; align-items: center; justify-content: center; margin: 0; padding: 0; font-size: 0.8rem; line-height: 1; text-align: center; }
  .element-type-label { align-self: center; margin-left: 0.7rem; padding: 0 0.35rem; color: var(--pax-sub, #474752); font-size: 0.7rem; white-space: nowrap; }
  .state { gap: 0.5rem; padding: 1.5rem; }
  :global(.row .anon-element) { margin-left: 0; }
  .keyboard-target-active { outline: 2px solid var(--pax-black); outline-offset: 2px; z-index: 1; }
  .keyboard-target-active::before { content: ""; position: absolute; top: -0.55rem; left: 50%; width: 0; height: 0; border-left: 0.32rem solid transparent; border-right: 0.32rem solid transparent; border-top: 0.42rem solid var(--pax-black); transform: translateX(-50%); pointer-events: none; }
  .header-assistant-button { display: flex; align-items: center; justify-content: center; width: 2.5rem; height: 2.5rem; border: 0; border-radius: 0.5rem; background: var(--pax-black); cursor: pointer; }
  .header-assistant-button.active, .header-assistant-button:hover { background: #2a2a2f; }
  .header-assistant-button img { width: 1.1rem; height: 1.1rem; filter: brightness(0) invert(1); }
</style>
